/**
 * Invites Index Page
 *
 * Lists all invites with search, filter, and pagination.
 *
 * @fileoverview Admin invites list page
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  useDisclosure,
  useToast,
  Icon,
} from '@chakra-ui/react';
import { MdSend } from 'react-icons/md';
import {
  InviteTable,
  InviteFilters,
  InviteStats,
  RevokeInviteModal,
  DeleteInviteModal,
} from '../../../components/admin';
import { useAdminInvites, useAdminInviteStats, useInviteActions } from '../../../hooks/useAdminInvites';
import type { AdminInvite } from '../../../types/admin';

/**
 * Invites Index Page Component
 *
 * Main page for managing invites.
 */
export const InvitesIndex: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const revokeModal = useDisclosure();
  const deleteModal = useDisclosure();

  const [selectedInvite, setSelectedInvite] = useState<AdminInvite | null>(null);

  const {
    invites,
    loading,
    pagination,
    filters,
    setPage,
    setSearch,
    setCombinedStatus,
    resetFilters,
    refetch,
  } = useAdminInvites();

  const { stats, loading: statsLoading, refetch: refetchStats } = useAdminInviteStats();
  const { revokeInvite, resendInvite, deleteInvite, loading: actionLoading } = useInviteActions();

  // Handle view invite
  const handleView = (invite: AdminInvite) => {
    navigate(`/admin/invites/${invite.id}`);
  };

  // Handle resend invite
  const handleResend = async (invite: AdminInvite) => {
    const result = await resendInvite(invite.id);
    if (result) {
      toast({
        title: 'Invite resent',
        description: `A new invite email has been sent to ${invite.email}.`,
        status: 'success',
        duration: 3000,
      });
      refetch();
      refetchStats();
    } else {
      toast({
        title: 'Something went wrong',
        description: "We couldn't resend the invite. Please try again.",
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Handle revoke invite
  const handleRevokeClick = (invite: AdminInvite) => {
    setSelectedInvite(invite);
    revokeModal.onOpen();
  };

  const handleRevokeConfirm = async () => {
    if (!selectedInvite) return;

    const result = await revokeInvite(selectedInvite.id);
    if (result) {
      toast({
        title: 'Invite revoked',
        description: `The invite for ${selectedInvite.email} has been revoked.`,
        status: 'warning',
        duration: 3000,
      });
      revokeModal.onClose();
      refetch();
      refetchStats();
    } else {
      toast({
        title: 'Something went wrong',
        description: "We couldn't revoke the invite. Please try again.",
        status: 'error',
        duration: 5000,
      });
    }
    setSelectedInvite(null);
  };

  // Handle delete invite
  const handleDeleteClick = (invite: AdminInvite) => {
    setSelectedInvite(invite);
    deleteModal.onOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedInvite) return;

    const success = await deleteInvite(selectedInvite.id);
    if (success) {
      toast({
        title: 'Invite deleted',
        description: `The invite for ${selectedInvite.email} has been deleted.`,
        status: 'success',
        duration: 3000,
      });
      deleteModal.onClose();
      refetch();
      refetchStats();
    } else {
      toast({
        title: 'Something went wrong',
        description: "We couldn't delete the invite. Please try again.",
        status: 'error',
        duration: 5000,
      });
    }
    setSelectedInvite(null);
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <HStack justify="space-between" wrap="wrap" gap={4}>
        <Box>
          <Heading size="lg">Invites</Heading>
          <Text color="gray.500" mt={1}>
            Manage user invitations
          </Text>
        </Box>
        <Button
          colorScheme="blue"
          leftIcon={<Icon as={MdSend} />}
          onClick={() => navigate('/admin/invites/new')}
        >
          Send Invite
        </Button>
      </HStack>

      {/* Stats */}
      <InviteStats stats={stats} loading={statsLoading} />

      {/* Filters */}
      <InviteFilters
        search={filters.search || ''}
        combinedStatus={filters.combinedStatus}
        onSearchChange={setSearch}
        onCombinedStatusChange={setCombinedStatus}
        onReset={resetFilters}
      />

      {/* Table */}
      <InviteTable
        invites={invites}
        loading={loading}
        pagination={pagination}
        onPageChange={setPage}
        onView={handleView}
        onResend={handleResend}
        onRevoke={handleRevokeClick}
        onDelete={handleDeleteClick}
      />

      {/* Revoke Modal */}
      <RevokeInviteModal
        invite={selectedInvite}
        isOpen={revokeModal.isOpen}
        onClose={revokeModal.onClose}
        onConfirm={handleRevokeConfirm}
        loading={actionLoading}
      />

      {/* Delete Modal */}
      <DeleteInviteModal
        invite={selectedInvite}
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.onClose}
        onConfirm={handleDeleteConfirm}
        loading={actionLoading}
      />
    </VStack>
  );
};

export default InvitesIndex;
